import type { TFunction } from "i18next";

export interface AutoFaqItem {
  id: string;
  category: string;
  question: string;
  answer: string;
}

export interface BuildAutoFaqInput {
  hotelName: string;
  destinationName?: string | null;
  address?: string | null;
  city?: string | null;
  country?: string | null;
  phone?: string | null;
  amenityNames?: string[];
  isPetFriendly?: boolean | null;
  // Optional room-derived data (present on dynamic detail page)
  checkInHour?: string | null;
  checkOutHour?: string | null;
  boardTypes?: string[];
  bedTypes?: string[];
  roomOptionsCount?: number;
  hasRefundable?: boolean;
  t: TFunction;
}

/**
 * Builds the auto-generated FAQ items from hotel data.
 * Shared between the dynamic hotel detail page and the static hotel page
 * so both surfaces render the same set of questions.
 */
export function buildAutoFaqItems(input: BuildAutoFaqInput): AutoFaqItem[] {
  const {
    hotelName,
    destinationName,
    address,
    city,
    country,
    phone,
    amenityNames = [],
    isPetFriendly,
    checkInHour,
    checkOutHour,
    boardTypes = [],
    bedTypes = [],
    roomOptionsCount,
    hasRefundable,
    t,
  } = input;

  const items: AutoFaqItem[] = [];
  const resolvedDestination =
    destinationName?.trim() ||
    [city, country].filter(Boolean).join(", ") ||
    "";

  // Destination (always render when we know where the hotel is)
  if (resolvedDestination) {
    items.push({
      id: "destination",
      category: t("hotelFaq.categories.location"),
      question: t("hotelFaq.destination.question", { hotelName }),
      answer: t("hotelFaq.destination.answer", {
        hotelName,
        destinationName: resolvedDestination,
      }),
    });
  }

  // Check-in
  items.push({
    id: "check-in",
    category: t("hotelFaq.categories.checkInOut"),
    question: t("hotelFaq.checkIn.question", { hotelName }),
    answer: checkInHour
      ? t("hotelFaq.checkIn.answerWithTime", { time: checkInHour, hotelName })
      : t("hotelFaq.checkIn.answerDefault", { hotelName }),
  });

  // Check-out
  items.push({
    id: "check-out",
    category: t("hotelFaq.categories.checkInOut"),
    question: t("hotelFaq.checkOut.question", { hotelName }),
    answer: checkOutHour
      ? t("hotelFaq.checkOut.answerWithTime", { time: checkOutHour, hotelName })
      : t("hotelFaq.checkOut.answerDefault", { hotelName }),
  });

  // Location / address
  if (address || city) {
    const fullAddress = [address, city, country].filter(Boolean).join(", ");
    items.push({
      id: "location",
      category: t("hotelFaq.categories.location"),
      question: t("hotelFaq.location.question", { hotelName }),
      answer: t("hotelFaq.location.answer", {
        address: fullAddress,
        hotelName,
      }),
    });
  }

  // Board types
  const uniqueBoardTypes = [...new Set(boardTypes.filter(Boolean))];
  if (uniqueBoardTypes.length > 0) {
    const hasBreakfast = uniqueBoardTypes.some((board) => {
      const lower = board?.toLowerCase() || "";
      return (
        lower.includes("breakfast") ||
        lower.includes("desayuno") ||
        lower.includes("café") ||
        lower.includes("half board") ||
        lower.includes("full board") ||
        lower.includes("all inclusive") ||
        lower.includes("media pensión") ||
        lower.includes("pensión completa")
      );
    });

    items.push({
      id: "meals",
      category: t("hotelFaq.categories.meals"),
      question: t("hotelFaq.meals.question", { hotelName }),
      answer: t("hotelFaq.meals.answer", {
        options: uniqueBoardTypes.join(", "),
        hotelName,
      }),
    });

    items.push({
      id: "breakfast",
      category: t("hotelFaq.categories.meals"),
      question: t("hotelFaq.breakfast.question", { hotelName }),
      answer: hasBreakfast
        ? t("hotelFaq.breakfast.answerYes", { hotelName })
        : t("hotelFaq.breakfast.answerDepends", { hotelName }),
    });
  }

  // Bed types
  const uniqueBedTypes = [...new Set(bedTypes.filter(Boolean))];
  if (uniqueBedTypes.length > 0) {
    items.push({
      id: "beds",
      category: t("hotelFaq.categories.rooms"),
      question: t("hotelFaq.beds.question", { hotelName }),
      answer: t("hotelFaq.beds.answer", {
        options: uniqueBedTypes.join(", "),
        hotelName,
      }),
    });
  }

  // Room options count
  if (roomOptionsCount && roomOptionsCount > 1) {
    items.push({
      id: "room-options",
      category: t("hotelFaq.categories.rooms"),
      question: t("hotelFaq.roomOptions.question", { hotelName }),
      answer: t("hotelFaq.roomOptions.answer", {
        count: roomOptionsCount,
        hotelName,
      }),
    });
  }

  // Cancellation (only when we know refundable status from live rooms)
  if (typeof hasRefundable === "boolean") {
    items.push({
      id: "cancellation",
      category: t("hotelFaq.categories.policies"),
      question: t("hotelFaq.cancellation.question", { hotelName }),
      answer: hasRefundable
        ? t("hotelFaq.cancellation.answerMixed", { hotelName })
        : t("hotelFaq.cancellation.answerNonRefundable", { hotelName }),
    });
  }

  // Pets
  if (isPetFriendly !== null && isPetFriendly !== undefined) {
    items.push({
      id: "pets",
      category: t("hotelFaq.categories.policies"),
      question: t("hotelFaq.pets.question", { hotelName }),
      answer: isPetFriendly
        ? t("hotelFaq.pets.answerYes", { hotelName })
        : t("hotelFaq.pets.answerNo", { hotelName }),
    });
  }

  // Amenities
  const lowerAmenities = amenityNames.map((a) => a.toLowerCase());
  const hasWifi = lowerAmenities.some(
    (a) => a.includes("wifi") || a.includes("internet")
  );
  if (hasWifi) {
    items.push({
      id: "wifi",
      category: t("hotelFaq.categories.amenities"),
      question: t("hotelFaq.wifi.question", { hotelName }),
      answer: t("hotelFaq.wifi.answerYes", { hotelName }),
    });
  }
  const hasParking = lowerAmenities.some(
    (a) =>
      a.includes("parking") ||
      a.includes("estacionamiento") ||
      a.includes("aparcamiento") ||
      a.includes("estacionamento")
  );
  if (hasParking) {
    items.push({
      id: "parking",
      category: t("hotelFaq.categories.amenities"),
      question: t("hotelFaq.parking.question", { hotelName }),
      answer: t("hotelFaq.parking.answerYes", { hotelName }),
    });
  }
  const hasPool = lowerAmenities.some(
    (a) => a.includes("pool") || a.includes("piscina")
  );
  if (hasPool) {
    items.push({
      id: "pool",
      category: t("hotelFaq.categories.amenities"),
      question: t("hotelFaq.pool.question", { hotelName }),
      answer: t("hotelFaq.pool.answerYes", { hotelName }),
    });
  }

  // Contact
  if (phone) {
    items.push({
      id: "contact",
      category: t("hotelFaq.categories.contact"),
      question: t("hotelFaq.contact.question", { hotelName }),
      answer: t("hotelFaq.contact.answer", { phone, hotelName }),
    });
  }

  return items;
}
